<template>
  <v-container fluid class="pa-0 pa-md-4 py-12">
    <v-card class="w-100 lookup-card">
      <v-card-title class="d-flex justify-space-between align-center text-h5 font-weight-bold">
        Vehicle Lookup
      </v-card-title>

      <v-card-subtitle class="mt-6">
        Inspect current SRP field values and per-source mapped values for a single VIN.
      </v-card-subtitle>

      <v-card-text class="pt-0">
        <v-divider class="mt-4 mb-6" style="opacity: 0.3"></v-divider>

        <div class="text-caption text-grey-darken-1 mb-6" style="font-style: italic">
          💡 Tip: Use this when a field looks wrong on-site — you can quickly see which source value is winning.
        </div>

        <!-- Controls Bar -->
        <v-sheet class="control-bar pa-3 mb-4" rounded>
          <v-row class="align-center" no-gutters>
            <v-col cols="12" md="9" class="pr-md-3 mb-3 mb-md-0">
              <v-combobox
                v-model="query"
                label="VIN"
                placeholder="Start typing… suggestions appear on focus"
                variant="outlined"
                density="comfortable"
                hide-details
                clearable
                :disabled="isLoading"
                :items="suggestions"
                item-title="vin"
                item-value="vin"
                :return-object="false"
                :loading="suggestionsLoading"
                :no-data-text="suggestionsLoaded ? 'No suggestions' : 'Focus to load suggestions'"
                @update:focused="handleFocusChange"
                @update:model-value="handleVinSelection"
                @keyup.enter="onSearch"
              >
                <template #append-inner>
                  <v-btn
                    color="primary"
                    size="small"
                    variant="tonal"
                    class="ml-2"
                    :loading="isLoading"
                    :disabled="!normalizedVin"
                    @click="onSearch"
                  >
                    Search
                  </v-btn>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12" md="3" class="d-flex ga-2 justify-md-end">
              <v-btn variant="text" color="primary" :disabled="isLoading" @click="reset">
                Reset
              </v-btn>
            </v-col>
          </v-row>

          <div class="text-caption text-grey-darken-1 mt-2">
            Account: <strong>{{ accountSettings.accountId || '—' }}</strong>
          </div>
        </v-sheet>

        <div v-if="errorMessage">
          <v-alert type="error" variant="tonal" density="comfortable">
            {{ errorMessage }}
          </v-alert>
        </div>

        <div v-else-if="isLoading">
          <div class="text-center pa-8">
            <v-progress-circular indeterminate color="primary" size="56" width="4" class="mb-4" />
            <div class="text-h6">Loading vehicle details...</div>
          </div>
        </div>

        <div v-else-if="vehicleNotFound">
          <div class="text-center pa-8">
            <v-icon icon="mdi-car-off" size="80" color="grey" class="mb-4" />
            <div class="text-h6 mb-2">No vehicle found</div>
            <div class="text-body-2 text-grey mb-4">
              No vehicle record was found for this VIN.
            </div>
          </div>
        </div>

        <div v-else-if="!result">
          <div class="text-center pa-8">
            <v-icon icon="mdi-car-off" size="80" color="grey" class="mb-4" />
            <div class="text-h6 mb-2">No vehicle loaded</div>
            <div class="text-body-2 text-grey mb-4">
              Enter a VIN above to load the current SRP object and per-source values.
            </div>
          </div>
        </div>

        <div v-else>
          <v-row>
            <v-col cols="12">
              <v-card variant="outlined" class="pa-4">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="text-subtitle-1 font-weight-bold">Vehicle Summary</div>
                  <div class="d-flex align-center ga-2">
                    <v-chip size="x-small" variant="outlined">{{ result.fieldCount }} fields</v-chip>
                    <v-chip size="x-small" variant="outlined">{{ availableSources.length }} sources</v-chip>
                  </div>
                </div>

                <!-- Mobile: stacks (cols=12). Tablet/Desktop: lays out horizontally. -->
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="text-caption text-medium-emphasis">VIN</div>
                    <div class="text-body-2 mt-1">
                      <code>{{ result.vin }}</code>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="text-caption text-medium-emphasis">Stock #</div>
                    <div class="text-body-2 mt-1">{{ summaryStockNumber }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-medium-emphasis">Year / Make / Model</div>
                    <div class="text-body-2 mt-1">{{ summaryYmm }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="2">
                    <div class="text-caption text-medium-emphasis">Condition</div>
                    <div class="text-body-2 mt-1">{{ summaryCondition }}</div>
                  </v-col>
                </v-row>

                <div class="mt-3 d-flex align-center ga-2 flex-wrap">
                  <span class="text-caption text-grey-darken-1">Sources:</span>
                  <v-chip
                    v-for="sourceId in availableSources"
                    :key="sourceId"
                    size="x-small"
                    variant="outlined"
                  >
                    {{ formatSourceTitle(sourceId) }}
                  </v-chip>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-tabs v-model="activeTab" density="comfortable" class="mb-2">
                <v-tab value="mapped">Mapped Fields</v-tab>
                <v-tab value="computed">Computed Fields</v-tab>
                <v-tab value="sources">Source Values</v-tab>
              </v-tabs>

              <v-window v-model="activeTab">
                <!-- Tab 1: SRP mapped fields (user-configurable) with winner highlighting -->
                <v-window-item value="mapped">
                  <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                      SRP Mapped Field Values
                    </v-card-title>
                    <v-card-text>
                      <v-data-table
                        :headers="tableHeadersComputed"
                        :items="tableItemsFiltered"
                        :items-per-page="-1"
                        :items-per-page-options="[-1, 25, 50, 100]"
                        density="comfortable"
                        fixed-header
                        height="70vh"
                      >
                        <template #top>
                          <v-row class="mb-2">
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="fieldSearch"
                                placeholder="Search fields…"
                                prepend-inner-icon="mdi-magnify"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                              />
                            </v-col>
                            <v-col cols="12" md="6" class="d-flex align-center justify-md-end ga-2 flex-wrap">
                              <v-chip
                                size="small"
                                class="filter-chip"
                                :variant="showDiffOnly ? 'flat' : 'outlined'"
                                :color="showDiffOnly ? 'primary' : 'default'"
                                @click="showDiffOnly = !showDiffOnly"
                              >
                                <v-icon start size="small">mdi-compare</v-icon>
                                Differences only
                              </v-chip>
                              <v-chip
                                size="small"
                                class="filter-chip"
                                :variant="hideEmpty ? 'flat' : 'outlined'"
                                :color="hideEmpty ? 'info' : 'default'"
                                @click="hideEmpty = !hideEmpty"
                              >
                                <v-icon start size="small">mdi-eye-off-outline</v-icon>
                                Hide empty
                              </v-chip>
                              <v-chip
                                v-if="mismatchCount > 0"
                                size="small"
                                class="filter-chip"
                                :variant="showMismatchOnly ? 'flat' : 'outlined'"
                                color="error"
                                @click="showMismatchOnly = !showMismatchOnly"
                              >
                                <v-icon start size="small">mdi-alert-circle-outline</v-icon>
                                Mismatch ({{ mismatchCount }})
                              </v-chip>
                              <v-btn
                                v-if="fieldSearch || showDiffOnly || hideEmpty || showMismatchOnly"
                                size="x-small"
                                variant="text"
                                @click="clearTableFilters"
                              >
                                Clear filters
                              </v-btn>
                            </v-col>
                          </v-row>
                          <div
                            class="text-caption text-grey-darken-1"
                            v-if="tableItemsFiltered.length !== tableItems.length"
                          >
                            Showing {{ tableItemsFiltered.length }} of {{ tableItems.length }} fields
                          </div>
                        </template>

                        <template #item="{ item, columns }">
                          <tr v-if="item.__group" class="group-header-row">
                            <td :colspan="columns.length" class="group-header-cell">
                              <div>{{ item.groupLabel }}</div>
                            </td>
                          </tr>
                          <tr v-else>
                            <td v-for="col in columns" :key="col.key as string">
                              <template v-if="col.key === 'field'">
                                <div class="d-flex align-center ga-2">
                                  <v-chip v-if="item._segment" size="x-small" color="primary" variant="tonal">
                                    {{ item._segment }}
                                  </v-chip>
                                  <span>{{ item.field }}</span>
                                  <v-tooltip location="bottom" max-width="520" color="red">
                                    <template #activator="{ props }">
                                      <v-icon
                                        v-bind="props"
                                        size="16"
                                        color="grey-darken-1"
                                        class="ml-1"
                                      >
                                        mdi-information-outline
                                      </v-icon>
                                    </template>
                                    <div class="text-caption">
                                      <div class="d-flex ga-2">
                                        <span class="font-weight-medium">Document:</span>
                                        <span>{{ item._srpValue ?? '—' }}</span>
                                      </div>
                                      <div class="d-flex ga-2 mt-1">
                                        <span class="font-weight-medium">Search:</span>
                                        <span>{{ item._openSearchValue ?? '—' }}</span>
                                      </div>
                                    </div>
                                  </v-tooltip>
                                </div>
                              </template>
                              <template v-else-if="col.key === '_default'">
                                <div>
                                  <ScrollHint>{{ item._default }}</ScrollHint>
                                </div>
                              </template>
                              <template v-else-if="col.key">
                                <div :class="{ 'winner-cell': item._winnerSourceId === col.key }">
                                  <ScrollHint>{{ item[col.key as string] }}</ScrollHint>
                                </div>
                              </template>
                            </td>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-window-item>

                <!-- Tab 2: Computed fields from SRP object -->
                <v-window-item value="computed">
                  <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                      SRP Computed Field Values
                    </v-card-title>
                    <v-card-text>
                      <v-row class="mb-2">
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="computedSearch"
                            placeholder="Search computed fields…"
                            prepend-inner-icon="mdi-magnify"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                          />
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="computedHeaders"
                        :items="computedItemsFiltered"
                        :items-per-page="25"
                        density="comfortable"
                        fixed-header
                        height="70vh"
                      >
                        <template #item.value="{ item }">
                          <ScrollHint>{{ (item as any).value }}</ScrollHint>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-window-item>

                <!-- Tab 3: Raw source values (one source at a time) -->
                <v-window-item value="sources">
                  <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                      Source Values
                    </v-card-title>
                    <v-card-text>
                      <div class="text-caption text-grey-darken-1 mb-3">
                        Flattened paths from stored feed payloads (bounded).
                        <span v-if="result?.rawFeedFieldsMeta?.truncatedSources?.length">
                          Truncated:
                          <strong>{{ result.rawFeedFieldsMeta.truncatedSources.map(formatSourceTitle).join(', ') }}</strong>
                        </span>
                      </div>

                      <v-row class="mb-2">
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="rawFeedSearch"
                            placeholder="Search raw source fields…"
                            prepend-inner-icon="mdi-magnify"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-radio-group v-model="rawSelectedSource" inline density="comfortable" hide-details>
                            <v-radio
                              v-for="sid in rawFeedSources"
                              :key="sid"
                              :label="formatSourceTitle(sid)"
                              :value="sid"
                            />
                          </v-radio-group>
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="rawFeedHeaders"
                        :items="rawFeedItemsForSelectedSource"
                        :items-per-page="25"
                        density="comfortable"
                        fixed-header
                        height="70vh"
                      >
                        <template #item.value="{ item }">
                          <ScrollHint>{{ (item as any).value }}</ScrollHint>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-window-item>
              </v-window>
            </v-col>
          </v-row>

          <v-expansion-panels class="mt-6" variant="accordion">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-subtitle-1 font-weight-bold">
                Raw Payload (debug)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <pre class="payload-pre">{{ prettyResult }}</pre>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { fetchInventory, type InventoryVehicle } from '@/services/inventory'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import { fetchVehicleFieldMappingConfig } from '@/services/fieldMappingService'
import { isVehicleLookupNotFoundError } from '@/utils/vehicleLookupErrors'

type VehicleLookupApiResponse = {
  vin: string
  jazelId: string
  s3Key: string
  fieldCount: number
  availableSources: string[]
  openSearchDocFound?: boolean
  fields: Record<
    string,
    {
      value: unknown
      openSearchValue?: unknown
      source: { feed: string; fieldName: string; segment: string } | null
      bySource: Record<string, unknown>
    }
  >
  rawFeedFieldsBySource?: Record<string, Array<{ path: string; value: string }>>
  rawFeedFieldsMeta?: { maxEntriesPerSource: number; truncatedSources: string[] }
  computedFields?: Array<{ field: string; value: unknown }>
}

type LookupQuery = string | InventoryVehicle | null
const query = ref<LookupQuery>('')
const isLoading = ref(false)
const errorMessage = ref<string>('')
const vehicleNotFound = ref(false)
const result = ref<VehicleLookupApiResponse | null>(null)

const ScrollHint = defineComponent({
  name: 'ScrollHint',
  setup(_props, { slots }) {
    const el = ref<HTMLElement | null>(null)
    const showFade = ref(false)
    let ro: ResizeObserver | null = null

    const recompute = () => {
      const node = el.value
      if (!node) return
      const isOverflowing = node.scrollHeight > node.clientHeight + 1
      const isAtBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1
      showFade.value = isOverflowing && !isAtBottom
    }

    const onScroll = () => recompute()

    onMounted(() => {
      void nextTick(() => {
        recompute()
        // One more pass after layout settles (tables can reflow after initial mount)
        requestAnimationFrame(recompute)
      })
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => recompute())
        if (el.value) ro.observe(el.value)
      }
      window.addEventListener('resize', recompute)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', recompute)
      if (ro) {
        ro.disconnect()
        ro = null
      }
    })

    // Ensure fade is correct after slot content updates / table re-renders.
    onUpdated(() => {
      void nextTick(recompute)
    })

    watch(
      () => slots.default?.(),
      () => {
        void nextTick(recompute)
      },
    )

    return () =>
      h(
        'div',
        {
          ref: el,
          class: ['cell-scroll', 'text-body-2', { 'cell-scroll--fade': showFade.value }],
          onScroll,
        },
        h('div', { class: 'cell-scroll__content' }, slots.default?.()),
      )
  },
})

const accountSettings = useAccountSettingsStore()
const route = useRoute()
const appConfig = useJazelAppConfig()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const suggestions = ref<InventoryVehicle[]>([])
const suggestionsLoading = ref(false)
const suggestionsLoaded = ref(false)

const fieldSearch = ref('')
const showDiffOnly = ref(false)
const showMismatchOnly = ref(false)
const hideEmpty = ref(false)
const activeTab = ref<'mapped' | 'computed' | 'sources'>('mapped')
const computedSearch = ref('')
const rawSelectedSource = ref<string>('')
const rawFeedSearch = ref('')

// Some SRP fields are intentionally transformed/sanitized before indexing or querying,
// so S3 vs OpenSearch vs "winner" comparisons can be noisy/expected.
// Keep the mismatch filter focused on actionable drift.
const MISMATCH_EXEMPT_FIELDS = new Set<string>([
  // OpenSearch/query-side transforms / derived fields
  'features',
  'features_structured',
  'features_json',
  'features_sidebar',
  // Often transformed/expanded
  'tech_specs',
  'options_json',
])

const isMismatchExemptField = (fieldName: unknown) =>
  MISMATCH_EXEMPT_FIELDS.has(String(fieldName ?? '').toLowerCase())

const rawFeedSources = computed(() => {
  const obj = result.value?.rawFeedFieldsBySource
  if (!obj || typeof obj !== 'object') return []
  return Object.keys(obj).filter(Boolean).sort((a, b) => a.localeCompare(b))
})

const rawFeedHeaders = [
  { title: 'Path', key: 'path', sortable: true, width: '240px' },
  { title: 'Value', key: 'value', sortable: false },
] as const

const rawFeedItemsFilteredBySource = (sourceId: string) => {
  const rows = (result.value?.rawFeedFieldsBySource?.[sourceId] || []) as Array<{ path: string; value: string }>
  const q = rawFeedSearch.value.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((r) => {
    const hay = `${r.path} ${r.value}`.toLowerCase()
    return hay.includes(q)
  })
}

const rawFeedItemsForSelectedSource = computed(() => {
  if (!rawSelectedSource.value) return []
  return rawFeedItemsFilteredBySource(rawSelectedSource.value)
})

const computedHeaders = [
  { title: 'Field', key: 'field', sortable: true, width: '240px' },
  { title: 'Value', key: 'value', sortable: false },
] as const

const computedItemsFiltered = computed(() => {
  const rows = (result.value?.computedFields || []) as Array<{ field: string; value: unknown }>
  const q = computedSearch.value.trim().toLowerCase()
  const mapped = rows.map((r) => ({ field: r.field, value: formatValue(r.value) }))
  if (!q) return mapped
  return mapped.filter((r) => `${r.field} ${r.value}`.toLowerCase().includes(q))
})

const queryText = computed(() => {
  const q = query.value
  if (typeof q === 'string') return q
  if (q && typeof q === 'object') return String(q.vin || '')
  return ''
})

const normalizedVin = computed(() => queryText.value.trim().toUpperCase())
const isValidVin = computed(() => normalizedVin.value.length === 17)

const tableHeaders = [
  // Placeholder; replaced by computed headers below.
] as const

const formatSourceLabel = (src: VehicleLookupApiResponse['fields'][string]['source']): string => {
  if (!src) return '—'
  const seg = src.segment ? ` (${src.segment})` : ''
  return `${src.feed}${seg}`
}

const formatSourceTitle = (sourceId: string): string => {
  // e.g. import_homenet -> homenet
  return String(sourceId || '').replace(/^import_/, '')
}

const availableSources = computed(() => result.value?.availableSources || [])

const defaultsByField = ref<Record<string, string>>({})

const tableHeadersComputed = computed(() => {
  const base: Array<{ title: string; key: string; sortable?: boolean; width?: string }> = [
    { title: 'Field', key: 'field', sortable: true, width: '200px' },
    { title: 'Default', key: '_default', sortable: false, width: '200px' },
  ]

  const sourceCols = availableSources.value.map((sourceId) => ({
    title: `${formatSourceTitle(sourceId)}`,
    key: sourceId,
    sortable: false,
    width: '200px',
  }))

  return [...base, ...sourceCols]
})

watch(
  rawFeedSources,
  (next) => {
    if (!rawSelectedSource.value && next.length > 0) {
      rawSelectedSource.value = next[0]
    }
    if (rawSelectedSource.value && next.length > 0 && !next.includes(rawSelectedSource.value)) {
      rawSelectedSource.value = next[0]
    }
  },
  { immediate: true },
)

// Field group labels mapping (same as VehicleFieldMapping)
const fieldGroupLabels: Record<string, string> = {
  status: 'STATUS',
  condition: 'STATUS',
  state: 'STATUS',

  year: 'CORE IDENTITY',
  make: 'CORE IDENTITY',
  model: 'CORE IDENTITY',
  trim: 'CORE IDENTITY',
  vin: 'CORE IDENTITY',
  stock_number: 'CORE IDENTITY',

  body_type: 'BODY & STYLE',
  cab_style: 'BODY & STYLE',
  doors: 'BODY & STYLE',

  price: 'PRICING',
  display_price: 'PRICING',
  msrp: 'PRICING',
  discount: 'PRICING',
  kbb_price: 'PRICING',
  incentive_amount: 'PRICING',

  exterior_color: 'COLORS',
  exterior_factory_color: 'COLORS',
  interior_color: 'COLORS',
  interior_factory_color: 'COLORS',

  mpg_combined: 'EV & MPG',
  mpg_city_average: 'EV & MPG',
  mpg_city_high: 'EV & MPG',
  mpg_city_low: 'EV & MPG',
  mpg_highway_average: 'EV & MPG',
  mpg_highway_high: 'EV & MPG',
  mpg_highway_low: 'EV & MPG',

  mileage: 'MILEAGE & AGE',
  date_in_stock: 'MILEAGE & AGE',
  date_in_stock_ts: 'MILEAGE & AGE',
  modified_date: 'MILEAGE & AGE',

  drivetrain: 'DRIVETRAIN & MECHANICAL',
  drivetrain_code: 'DRIVETRAIN & MECHANICAL',
  engine: 'DRIVETRAIN & MECHANICAL',
  engine_displacement: 'DRIVETRAIN & MECHANICAL',
  engine_cylinders: 'DRIVETRAIN & MECHANICAL',
  engine_horsepower: 'DRIVETRAIN & MECHANICAL',
  engine_torque: 'DRIVETRAIN & MECHANICAL',
  engine_fuel_tank_capacity: 'DRIVETRAIN & MECHANICAL',
  transmission: 'DRIVETRAIN & MECHANICAL',
  transmission_description: 'DRIVETRAIN & MECHANICAL',

  fuel_type: 'FUEL TYPES',
  fuel_types: 'FUEL TYPES',

  passenger_capacity: 'SEATING & CAPACITY',
  passenger_capacity_text: 'SEATING & CAPACITY',

  features: 'FEATURES & EQUIPMENT',
  generic_equipment: 'FEATURES & EQUIPMENT',
  entertainment_features: 'FEATURES & EQUIPMENT',
  exterior_features: 'FEATURES & EQUIPMENT',
  interior_features: 'FEATURES & EQUIPMENT',
  mechanical_features: 'FEATURES & EQUIPMENT',
  safety_features: 'FEATURES & EQUIPMENT',
  packages: 'FEATURES & EQUIPMENT',
  options_json: 'FEATURES & EQUIPMENT',
  tech_specs: 'FEATURES & EQUIPMENT',
}

const getFieldGroupLabel = (fieldName: string): string | null => {
  const key = String(fieldName || '').toLowerCase()
  return fieldGroupLabels[key] || null
}

// Helper: resolve winner source ID from info.source.feed (V1 format like "vauto") to match availableSources (V2 format like "import_vauto")
const resolveWinnerSourceId = (feedName: string | null | undefined): string | null => {
  if (!feedName) return null
  const feed = String(feedName).trim()
  if (!feed) return null
  
  // Try exact match first
  if (availableSources.value.includes(feed)) return feed
  
  // Try with import_ prefix
  const withImport = feed.startsWith('import_') ? feed : `import_${feed}`
  if (availableSources.value.includes(withImport)) return withImport
  
  // Try without import_ prefix
  const withoutImport = feed.replace(/^import_/, '')
  if (availableSources.value.includes(withoutImport)) return withoutImport
  
  return null
}

const tableItems = computed(() => {
  const fields = result.value?.fields || {}
  
  // Create field rows
  const fieldRows = Object.entries(fields).map(([field, info]) => {
    const srpValue = formatValue(info?.value)
    const osValue = formatValue((info as any)?.openSearchValue)
    const drift = srpValue !== '—' && osValue !== '—' && srpValue !== osValue
    const winnerSourceId = resolveWinnerSourceId(info?.source?.feed)

    const row: Record<string, any> = {
      field,
      // Keep SRP/OpenSearch values for tooltip + drift filtering (not table columns).
      _srpValue: srpValue,
      _openSearchValue: osValue,
      _drift: drift,
      _default: defaultsByField.value[field] ?? '—',
      _segment: info?.source?.segment || null,
      _winnerSourceId: winnerSourceId,
      _mappedWinnerValue: '—',
      _groupLabel: getFieldGroupLabel(field),
    }
    for (const sourceId of availableSources.value) {
      row[sourceId] = formatValue(info?.bySource?.[sourceId])
    }
    row._mappedWinnerValue = winnerSourceId ? (row[winnerSourceId] ?? '—') : '—'
    return row
  })
  
  // Sort by group, then by field name
  fieldRows.sort((a, b) => {
    const groupA = a._groupLabel || 'ZZZZ_OTHER'
    const groupB = b._groupLabel || 'ZZZZ_OTHER'
    if (groupA !== groupB) return groupA.localeCompare(groupB)
    return String(a.field).localeCompare(String(b.field))
  })
  
  // Inject group header rows
  const rows: any[] = []
  let currentGroup: string | null = null
  
  for (const row of fieldRows) {
    const groupLabel = row._groupLabel
    if (groupLabel && groupLabel !== currentGroup) {
      rows.push({
        __group: true,
        groupLabel,
        field: `__group_${groupLabel}`,
      })
      currentGroup = groupLabel
    }
    rows.push(row)
  }
  
  return rows
})

const isEmptyDisplayValue = (v: unknown) => v === '—'

const rowHasDifferences = (row: Record<string, any>) => {
  const vals = availableSources.value.map((sid) => row?.[sid]).filter((v) => v !== undefined)
  const nonEmpty = vals.filter((v) => !isEmptyDisplayValue(v))
  
  // Need at least 2 sources with values to compare
  if (nonEmpty.length <= 1) return false
  
  // Check if the values differ
  const first = String(nonEmpty[0])
  return nonEmpty.some((v) => String(v) !== first)
}

const rowHasDrift = (row: Record<string, any>) => row?._drift === true

const valuesDiffer = (...vals: Array<unknown>) => {
  const cleaned = vals
    .map((v) => (v === null || v === undefined ? '—' : String(v)))
    .map((v) => v.trim())
    .filter((v) => v !== '' && v !== '—')
  if (cleaned.length < 2) return false
  return new Set(cleaned).size > 1
}

const rowHasMismatchS3OsWinner = (row: Record<string, any>) =>
  !isMismatchExemptField(row?.field) &&
  valuesDiffer(row?._srpValue, row?._openSearchValue, row?._mappedWinnerValue)

const mismatchCount = computed(() => {
  // Count only real field rows (exclude group headers)
  return tableItems.value.filter((row) => !row?.__group && rowHasMismatchS3OsWinner(row)).length
})

const tableItemsFiltered = computed(() => {
  const q = fieldSearch.value.trim().toLowerCase()
  
  // Filter field rows (not group headers)
  const filteredFieldRows = tableItems.value.filter((row) => {
    // Skip group header rows for now
    if (row.__group) return false
    
    // Hide empty: check if ALL sources AND default are empty
    if (hideEmpty.value) {
      const allEmpty =
        availableSources.value.every((sid) => isEmptyDisplayValue(row?.[sid])) &&
        isEmptyDisplayValue(row?._default) &&
        isEmptyDisplayValue(row?._srpValue) &&
        isEmptyDisplayValue(row?._openSearchValue) &&
        isEmptyDisplayValue(row?._mappedWinnerValue)
      if (allEmpty) return false
    }
    
    if (showMismatchOnly.value && !rowHasMismatchS3OsWinner(row)) return false
    if (showDiffOnly.value && !rowHasDifferences(row) && !rowHasDrift(row) && !rowHasMismatchS3OsWinner(row)) return false
    if (!q) return true
    const hay = [
      row.field,
      row._srpValue,
      row._openSearchValue,
      row._mappedWinnerValue,
      row._drift ? 'yes' : 'no',
      row._segment,
      row._default,
      ...availableSources.value.map((sid) => row?.[sid]),
    ]
      .map((v) => String(v ?? '').toLowerCase())
      .join(' ')
    return hay.includes(q)
  })
  
  // Re-inject group headers before filtered fields
  const rows: any[] = []
  let currentGroup: string | null = null
  
  for (const row of filteredFieldRows) {
    const groupLabel = row._groupLabel
    if (groupLabel && groupLabel !== currentGroup) {
      rows.push({
        __group: true,
        groupLabel,
        field: `__group_${groupLabel}`,
      })
      currentGroup = groupLabel
    }
    rows.push(row)
  }
  
  return rows
})

const prettyResult = computed(() => JSON.stringify(result.value, null, 2))

const summaryStockNumber = computed(() => {
  const f = result.value?.fields || {}
  return (
    (f.stock_number?.value as any) ??
    (f.stock?.value as any) ??
    (f.Stock?.value as any) ??
    '—'
  )
})

const summaryYmm = computed(() => {
  const f = result.value?.fields || {}
  const year = (f.year?.value as any) ?? (f.Year?.value as any)
  const make = (f.make?.value as any) ?? (f.Make?.value as any)
  const model = (f.model?.value as any) ?? (f.Model?.value as any)
  const parts = [year, make, model].filter((p) => p !== undefined && p !== null && String(p).trim() !== '')
  return parts.length ? parts.join(' ') : '—'
})

const summaryCondition = computed(() => {
  const f = result.value?.fields || {}
  return (
    (f.condition?.value as any) ??
    (f.Condition?.value as any) ??
    (f.NewUsed?.value as any) ??
    '—'
  )
})

const formatValue = (v: unknown) => {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string' && v.trim() === '') return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

const loadDefaultsByField = async () => {
  const jamsEndpoint = appConfig.jamsBackendUrl
  const accountId = accountSettings.accountId
  const token = accountSettings.sessionToken
  if (!jamsEndpoint || !accountId || !token) return

  const cfg = await fetchVehicleFieldMappingConfig(jamsEndpoint, accountId, token)
  const raw = (cfg as any)?.config && typeof (cfg as any).config === 'object' ? (cfg as any).config : cfg
  const mappings = raw?.mapping_config?.all?.field_mapping?.mappings || []

  const out: Record<string, string> = {}
  for (const m of mappings) {
    const field = String(m?.field || '').trim()
    if (!field) continue
    const dv = m?.default_value ?? m?.policy?.default_value
    if (dv === undefined || dv === null || String(dv).trim() === '') continue
    out[field] = formatValue(dv)
  }

  defaultsByField.value = out
}

watch(
  () => [accountSettings.accountId, accountSettings.sessionToken, appConfig.jamsBackendUrl] as const,
  () => {
    void loadDefaultsByField()
  },
  { immediate: true },
)

const loadSuggestions = async () => {
  if (suggestionsLoading.value || suggestionsLoaded.value) return
  if (!accountSettings.accountId) return

  suggestionsLoading.value = true
  try {
    const res = await fetchInventory(endpoint, {
      jazelId: accountSettings.accountId,
      limit: 200,
      offset: 0,
    })

    const vehicles = Array.isArray(res?.vehicles) ? res.vehicles : []
    // Keep only sane VINs and de-dupe (API may include repeats across feed/manual).
    const deduped = new Map<string, InventoryVehicle>()
    for (const v of vehicles) {
      const vin = String(v?.vin || '').trim().toUpperCase()
      if (vin.length !== 17) continue
      if (!deduped.has(vin)) deduped.set(vin, { ...v, vin })
    }
    suggestions.value = Array.from(deduped.values())
    suggestionsLoaded.value = true
  } catch (e) {
    console.warn('Vehicle lookup: failed to load suggestions', e)
    // Don’t block the page if suggestions fail.
    suggestionsLoaded.value = true
    suggestions.value = []
  } finally {
    suggestionsLoading.value = false
  }
}

const handleFocusChange = (focused: boolean) => {
  if (!focused) return
  loadSuggestions()
}

const handleVinSelection = (value: string | InventoryVehicle | null) => {
  // Auto-search when a VIN is selected from the dropdown
  if (!value) return
  
  const vinStr = typeof value === 'string' ? value : (value?.vin || '')
  const normalized = vinStr.trim().toUpperCase()
  
  // Only auto-search if it's a valid VIN and matches one from suggestions
  if (normalized.length === 17 && suggestions.value.some((s) => s.vin === normalized)) {
    // Defer search to next tick to ensure v-model is fully updated
    nextTick(() => {
      onSearch()
    })
  }
}

const fetchVehicleLookup = async (
  vinOrStock: string,
  opts?: { includeRawFeeds?: boolean; includeComputedFields?: boolean },
): Promise<VehicleLookupApiResponse> => {
  // For now: VIN-only lookup (stock will be supported later)
  const vin = String(vinOrStock || '').trim().toUpperCase()

  const base = String(endpoint).replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-lookup`)
  url.searchParams.set('jazelId', accountSettings.accountId)
  url.searchParams.set('vin', vin)
  if (opts?.includeRawFeeds) url.searchParams.set('includeRawFeeds', '1')
  if (opts?.includeComputedFields) url.searchParams.set('includeComputedFields', '1')

  const res = await fetch(url.toString(), { method: 'GET' })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const error = new Error(text || `Lookup failed (HTTP ${res.status})`) as Error & { status?: number }
    error.status = res.status
    throw error
  }
  return (await res.json()) as VehicleLookupApiResponse
}

const runLookup = async (params?: { resetResult?: boolean }) => {
  const vin = normalizedVin.value
  if (!vin) return

  errorMessage.value = ''
  vehicleNotFound.value = false
  isLoading.value = true
  if (params?.resetResult !== false) result.value = null

  try {
    if (!accountSettings.accountId) {
      throw new Error('Missing accountId (jazelId). Please ensure you are logged in / embedded correctly.')
    }
    if (!isValidVin.value) {
      throw new Error('Please enter a valid 17-character VIN.')
    }
    const includeComputedFields = activeTab.value === 'computed'
    const includeRawFeeds = activeTab.value === 'sources'
    result.value = await fetchVehicleLookup(vin, { includeComputedFields, includeRawFeeds })
  } catch (e) {
    console.error(e)
    if (isVehicleLookupNotFoundError(e)) {
      vehicleNotFound.value = true
      result.value = null
      errorMessage.value = ''
    } else {
      errorMessage.value = e instanceof Error ? e.message : 'Failed to fetch vehicle.'
    }
  } finally {
    isLoading.value = false
  }
}

const onSearch = async () => runLookup({ resetResult: true })

const routeVin = computed(() => {
  const vin = route.query.vin
  return typeof vin === 'string' ? vin.trim().toUpperCase() : ''
})

const applyVinFromRoute = async () => {
  const vin = routeVin.value
  if (!vin || !accountSettings.accountId) return
  if (queryText.value === vin && result.value) return

  query.value = vin
  await nextTick()
  await onSearch()
}

watch(
  [routeVin, () => accountSettings.accountId],
  () => {
    void applyVinFromRoute()
  },
  { immediate: true },
)

watch(activeTab, async () => {
  // Switch tabs => re-run lookup to request the right debug payload for that tab.
  if (isLoading.value) return
  if (!normalizedVin.value || !accountSettings.accountId) return
  if (!isValidVin.value) return
  await runLookup({ resetResult: false })
})

const reset = () => {
  query.value = ''
  errorMessage.value = ''
  vehicleNotFound.value = false
  result.value = null
  rawFeedSearch.value = ''
  computedSearch.value = ''
  activeTab.value = 'mapped'
}

const clearTableFilters = () => {
  fieldSearch.value = ''
  showDiffOnly.value = false
  hideEmpty.value = false
  showMismatchOnly.value = false
}

watch(
  mismatchCount,
  (count) => {
    // If mismatches go away (e.g., data refreshed), auto-disable the toggle to avoid confusing "empty results".
    if (count === 0) showMismatchOnly.value = false
  },
  { immediate: true },
)
</script>

<style scoped>
.lookup-card {
  overflow: visible !important;
}

.control-bar {
  background-color: #f8fafc;
  color: #111827;
  border: 1px solid #e5e7eb;
}

.cell-scroll {
  max-width: 200px;
  max-height: 200px;
  overflow: auto; /* vertical + horizontal when needed */
}

.cell-scroll__content {
  padding: 6px 10px;
  word-break: break-word;
}

.cell-scroll--fade .cell-scroll__content {
  padding-bottom: 24px; /* keep text clear of the fade overlay */
}

.cell-scroll--fade::after {
  --fade-h: 24px;
  content: '';
  display: block;
  position: sticky;
  bottom: 0;
  height: var(--fade-h);
  margin-top: calc(-1 * var(--fade-h));
  pointer-events: none;
  /* Vuetify theme surface is stored as "r,g,b" */
  background: linear-gradient(
    to bottom,
    rgba(var(--v-theme-surface), 0),
    rgb(var(--v-theme-surface))
  );
}

.winner-cell {
  background-color: rgba(33, 150, 243, 0.1) !important;
  font-weight: 500;
  padding: 12px !important;
}

.group-header-row {
  background-color: #FFF !important;
}

.group-header-cell {
  background-color: #FFF !important;
  border-left: none !important;
  border-right: none !important;
  padding: 0 12px !important;
}

.group-header-cell > div {
  margin-top: 16px;
  padding-top: 8px;
  padding-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.65);
  border-top: none;
  background-color: transparent;
}

:deep(.v-data-table tbody td) {
  padding: 12px !important;
}

:deep(.v-data-table thead th),
:deep(.v-table thead th),
:deep(.v-data-table__th),
:deep(.v-data-table__th--sticky) {
  background-color: #f5f5f5 !important;
  text-transform: capitalize !important;
}

:deep(.v-data-table__th .v-data-table-header__content > span),
:deep(.v-data-table__th--sticky .v-data-table-header__content >span) {
  font-weight: bold !important;
}

.payload-pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 12px;
  max-height: 420px;
  overflow: auto;
}
</style>


